import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaTimes, FaCheckCircle, FaHistory, FaSyncAlt } from 'react-icons/fa';
import { liveEventApi } from '../../api/scorecard';
import styles from './ReadOnlyScorecard.module.css';

const ReadOnlyScorecard = ({ event, onClose, onRefresh }) => {
  const [activeTab, setActiveTab] = useState('score');
  const [liveSession, setLiveSession] = useState(event?.liveSession);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [currentTime, setCurrentTime] = useState(Date.now()); // For live timer updates

  const schema = liveSession?.scorecard?.templateSnapshot?.schema;
  const scorecard = liveSession?.scorecard;
  const homeScore = scorecard?.score?.home || 0;
  const awayScore = scorecard?.score?.away || 0;
  const homeStats = scorecard?.statistics?.home || {};
  const awayStats = scorecard?.statistics?.away || {};
  
  // Client-side timer ticker (updates every second when running)
  useEffect(() => {
    if (scorecard?.timer?.isRunning) {
      const interval = setInterval(() => {
        setCurrentTime(Date.now());
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [scorecard?.timer?.isRunning]);
  
  // Get timer display
  const getTimerDisplay = () => {
    if (!scorecard?.timer) return '--:--';
    
    // If timer is running, calculate current time
    if (scorecard.timer.isRunning && scorecard.timer.periodStartedAt) {
      const startTime = new Date(scorecard.timer.periodStartedAt).getTime();
      const elapsedMs = currentTime - startTime;
      const totalSeconds = Math.floor(elapsedMs / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    
    // If paused, use elapsedSeconds
    const elapsed = scorecard.timer.elapsedSeconds || 0;
    const minutes = Math.floor(elapsed / 60);
    const seconds = elapsed % 60;
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };
  
  const getTimerStatus = () => {
    if (!scorecard?.timer) return 'Not started';
    if (scorecard.timer.isRunning) return 'Running';
    return 'Paused';
  };

  // Sync with parent event prop when it changes
  useEffect(() => {
    if (event?.liveSession?.scorecard) {
      console.log('🔄 Parent event updated, syncing local state');
      setLiveSession(event.liveSession);
      setLastUpdated(new Date());
    }
  }, [event?.liveSession?.scorecard?.lastUpdated]);

  // Fetch latest scorecard data
  const refreshScorecard = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Refreshing scorecard for event:', event._id);
      
      // If parent provided a refresh callback, use it
      if (onRefresh) {
        console.log('🔄 Using parent refresh callback');
        await onRefresh();
        setLastUpdated(new Date());
        console.log('✅ Parent refreshed successfully');
        return;
      }
      
      // Otherwise try to fetch from API (managers only)
      const response = await liveEventApi.getEvent(event._id);
      console.log('📦 API Response:', response);
      
      // The API returns the event directly
      const updatedEvent = response.event || response;
      console.log('📊 Updated event data:', {
        hasLiveSession: !!updatedEvent.liveSession,
        hasScorecard: !!updatedEvent.liveSession?.scorecard,
        score: updatedEvent.liveSession?.scorecard?.score,
        lastUpdated: updatedEvent.liveSession?.scorecard?.lastUpdated
      });
      
      if (updatedEvent.liveSession?.scorecard) {
        setLiveSession(updatedEvent.liveSession);
        setLastUpdated(new Date());
        console.log('✅ Scorecard refreshed successfully at:', new Date().toLocaleTimeString());
        console.log('✅ New scores:', {
          home: updatedEvent.liveSession.scorecard.score.home,
          away: updatedEvent.liveSession.scorecard.score.away
        });
      } else {
        console.warn('⚠️ No scorecard data in response');
      }
    } catch (err) {
      console.error('❌ Error refreshing scorecard:', err);
      console.error('Error details:', err.message);
      // Don't show error to user - just log it
      // The data will refresh again in 10 seconds
    } finally {
      setLoading(false);
    }
  }, [event._id, onRefresh]);

  // Auto-refresh every 30 seconds (reduced from 10s to reduce server load)
  useEffect(() => {
    // Don't auto-refresh on initial open - use existing data
    // Only auto-refresh after 30 seconds
    const interval = setInterval(() => {
      console.log('⏰ Auto-refresh triggered (30s interval)');
      refreshScorecard();
    }, 30000); // 30 seconds (reduced load)

    return () => {
      console.log('🛑 Stopping auto-refresh');
      clearInterval(interval);
    };
  }, [refreshScorecard]);

  // Validate event structure
  if (!liveSession?.scorecard?.templateSnapshot?.schema) {
    return createPortal(
      <div className={styles.modalOverlay}>
        <div className={styles.modal}>
          <div className={styles.header}>
            <h2>Error</h2>
            <button onClick={onClose} className={styles.closeButton}>
              <FaTimes />
            </button>
          </div>
          <div className={styles.errorBanner}>
            Invalid scorecard data. Please try again.
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // Get manager name if available
  const getManagerName = () => {
    if (liveSession.startedBy?.firstName && liveSession.startedBy?.lastName) {
      return `${liveSession.startedBy.firstName} ${liveSession.startedBy.lastName}`;
    }
    return 'Event Manager';
  };

  const renderScoreTab = () => (
    <div className={styles.scoreTab}>
      <div className={styles.scoreDisplay}>
        <div className={styles.teamScore}>
          <div className={styles.teamLabel}>HOME</div>
          <div className={styles.teamName}>{event.teams?.home?.name || 'Home Team'}</div>
          <div className={styles.scoreValue}>{homeScore}</div>
        </div>

        <div className={styles.vs}>VS</div>

        <div className={styles.teamScore}>
          <div className={styles.teamLabel}>AWAY</div>
          <div className={styles.teamName}>{event.teams?.away?.name || 'Away Team'}</div>
          <div className={styles.scoreValue}>{awayScore}</div>
        </div>
      </div>
    </div>
  );

  const renderStatisticsTab = () => (
    <div className={styles.statisticsTab}>
      <div className={styles.statsColumns}>
        <div className={styles.statsColumn}>
          <h3>Home Team</h3>
          {Object.entries(homeStats).map(([key, value]) => (
            <div key={key} className={styles.statRow}>
              <span className={styles.statLabel}>{key}:</span>
              <span className={styles.statValue}>{value}{typeof value === 'number' && key.includes('percentage') ? '%' : ''}</span>
            </div>
          ))}
          {Object.keys(homeStats).length === 0 && (
            <p className={styles.noData}>No statistics recorded</p>
          )}
        </div>

        <div className={styles.statsColumn}>
          <h3>Away Team</h3>
          {Object.entries(awayStats).map(([key, value]) => (
            <div key={key} className={styles.statRow}>
              <span className={styles.statLabel}>{key}:</span>
              <span className={styles.statValue}>{value}{typeof value === 'number' && key.includes('percentage') ? '%' : ''}</span>
            </div>
          ))}
          {Object.keys(awayStats).length === 0 && (
            <p className={styles.noData}>No statistics recorded</p>
          )}
        </div>
      </div>
    </div>
  );

  const renderEventLogTab = () => (
    <div className={styles.eventLogTab}>
      <div className={styles.eventLogList}>
        <h3><FaHistory /> Match Events</h3>
        {!scorecard.events || scorecard.events.length === 0 ? (
          <p className={styles.noEvents}>No events logged yet</p>
        ) : (
          <div className={styles.events}>
            {[...scorecard.events].reverse().map((evt, index) => {
              const eventIcons = {
                'goal': '⚽', 'yellowCard': '🟨', 'redCard': '🟥',
                'basket': '🏀', 'threePointer': '🏀', 'ace': '🎾',
                'touchdown': '🏈', 'fieldGoal': '🏈'
              };
              const icon = eventIcons[evt.type] || '📝';

              return (
                <div key={index} className={styles.eventItem}>
                  <div className={styles.eventTime}>
                    {evt.minute ? `${evt.minute}'` : 'Start'}
                    {evt.period && <span className={styles.eventPeriod}> - {evt.period}</span>}
                  </div>
                  <div className={styles.eventContent}>
                    <div className={styles.eventHeader}>
                      <span className={styles.eventIcon}>{icon}</span>
                      <div className={styles.eventTeam}>
                        {evt.team === 'home' ? event.teams?.home?.name || 'Home' : event.teams?.away?.name || 'Away'}
                      </div>
                    </div>
                    <div className={styles.eventDescription}>{evt.description}</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  const renderPeriodsTab = () => (
    <div className={styles.periodsTab}>
      {schema?.periods?.enabled && scorecard.score.periods && (
        <div className={styles.periodsSection}>
          <h3>Score by Period</h3>
          <table className={styles.periodsTable}>
            <thead>
              <tr>
                <th>Period</th>
                <th>Home</th>
                <th>Away</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {scorecard.score.periods.map((period, index) => (
                <tr key={index} className={period.isActive ? styles.activePeriod : ''}>
                  <td>{period.name}</td>
                  <td>{period.homeScore}</td>
                  <td>{period.awayScore}</td>
                  <td>
                    {period.isActive ? (
                      <span className={styles.statusActive}>● Active</span>
                    ) : period.finishedAt ? (
                      <span className={styles.statusFinished}>✓ Finished</span>
                    ) : (
                      <span className={styles.statusPending}>○ Pending</span>
                    )}
                  </td>
                </tr>
              ))}
              
              {scorecard.score.overtimePeriods?.map((period, index) => (
                <tr key={`ot-${index}`} className={period.isActive ? styles.activePeriod : ''}>
                  <td>⏱️ {period.name}</td>
                  <td>{period.homeScore}</td>
                  <td>{period.awayScore}</td>
                  <td>
                    {period.isActive ? (
                      <span className={styles.statusActive}>● Active</span>
                    ) : period.finishedAt ? (
                      <span className={styles.statusFinished}>✓ Finished</span>
                    ) : (
                      <span className={styles.statusPending}>○ Pending</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return createPortal(
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <h2>{event.title}</h2>
            <div className={styles.headerInfo}>
              <p className={styles.liveIndicator}>
                <span className={styles.liveDot}></span>
                LIVE
              </p>
              <div className={styles.timerDisplay}>
                <span className={styles.timerLabel}>Timer:</span>
                <span className={styles.timerTime}>{getTimerDisplay()}</span>
                {scorecard?.timer && (
                  <span className={styles.timerStatus}>
                    {scorecard.timer.isRunning ? '▶ Running' : '⏸ Paused'}
                  </span>
                )}
                {!scorecard?.timer && (
                  <span className={styles.timerStatus}>⏸ Not started</span>
                )}
              </div>
              <span className={styles.viewOnlyBadge}>(View Only)</span>
            </div>
          </div>

          <div className={styles.headerActions}>
            <button 
              onClick={refreshScorecard} 
              className={styles.refreshButton}
              disabled={loading}
              title="Refresh scorecard data"
            >
              <FaSyncAlt className={loading ? styles.spinning : ''} />
            </button>
            <button onClick={onClose} className={styles.closeButton}>
              <FaTimes />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className={styles.tabs}>
          <button 
            className={`${styles.tab} ${activeTab === 'score' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('score')}
          >
            Score
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'statistics' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('statistics')}
          >
            Statistics
          </button>
          <button 
            className={`${styles.tab} ${activeTab === 'events' ? styles.activeTab : ''}`}
            onClick={() => setActiveTab('events')}
          >
            Events ({scorecard.events?.length || 0})
          </button>
          {schema?.periods?.enabled && (
            <button 
              className={`${styles.tab} ${activeTab === 'periods' ? styles.activeTab : ''}`}
              onClick={() => setActiveTab('periods')}
            >
              Periods
            </button>
          )}
        </div>

        {/* Content */}
        <div className={styles.content}>
          {activeTab === 'score' && renderScoreTab()}
          {activeTab === 'statistics' && renderStatisticsTab()}
          {activeTab === 'events' && renderEventLogTab()}
          {activeTab === 'periods' && renderPeriodsTab()}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <div className={styles.footerLeft}>
            <span className={styles.refreshIndicator}>
              {loading ? '⟳ Refreshing...' : '↻ Auto-refreshes every 30s • Click 🔄 to refresh now'}
            </span>
            <span className={styles.lastUpdatedTime}>
              Last updated: {lastUpdated.toLocaleTimeString()}
            </span>
          </div>
          <span className={styles.managedBy}>
            Managed by: {getManagerName()}
          </span>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ReadOnlyScorecard;

